import { create } from 'zustand';
import { apiFetch, configureApi, ApiError, API_BASE } from '../lib/api';
import { guardarSala, type SalaEntrada } from '../lib/sala';

/**
 * Sesión del usuario (C5: users + identities). El access token vive SOLO en
 * memoria; el refresh es cookie httpOnly del backend. Al recargar la página se
 * rehidrata con /auth/me (que usa la cookie para emitir un token nuevo).
 */
export type RolGlobal =
  | 'superadmin'
  | 'facilitador' | 'admin_org'
  | 'decisor'
  | 'participante'
  | 'observador'
  | 'miembro';

export interface AuthUser {
  id: string;
  nombre: string;
  correo?: string | null;
  rol: RolGlobal;
  org_id?: string | null;
  org_nombre?: string | null;
}

/** Fase de hidratación: 'idle' antes de intentar /auth/me. */
export type AuthStatus = 'idle' | 'hydrating' | 'authenticated' | 'anonymous';

interface LoginLocalInput {
  correo: string;
  password: string;
}

interface LoginSalaInput {
  /** Código de sesión mostrado en la Pantalla. */
  codigo: string;
  /** PIN de 4 dígitos de la sala. */
  pin: string;
  /**
   * Nombre del participante (Lote 6, auto-registro por sala). Si viaja, el
   * backend intenta primero el PIN compartido del proceso y crea (o reutiliza)
   * al participante por nombre; sin él, el flujo por PIN personal sigue igual.
   */
  nombre?: string;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  status: AuthStatus;
  /** Último error humano de un intento de login (se limpia al reintentar). */
  error: string | null;

  /** Rehidrata la sesión desde la cookie de refresh (/auth/me). */
  hydrate: () => Promise<void>;
  loginLocal: (input: LoginLocalInput) => Promise<void>;
  loginSala: (input: LoginSalaInput) => Promise<void>;
  /** Redirige al OAuth de Google; con `sala`, al volver queda como miembro del proceso de esa sesión. */
  loginGoogle: (sala?: string) => void;
  logout: () => Promise<void>;
  clearError: () => void;
}

/** Usuario tal como lo devuelve el backend (email/rol_global). */
interface RawUser {
  id: string;
  nombre: string;
  email?: string | null;
  correo?: string | null;
  rol_global?: string | null;
  rol?: string | null;
  org_id?: string | null;
  org_nombre?: string | null;
  avatar_url?: string | null;
}
interface RawSession {
  access_token: string;
  user: RawUser;
  /** Solo en el ingreso por sala: la sala que abrió el código (EL CÓDIGO MANDA). */
  sala?: SalaEntrada | null;
}

/** Normaliza el usuario del backend (email/rol_global) al modelo del front. */
function toAuthUser(raw: RawUser): AuthUser {
  return {
    id: raw.id,
    nombre: raw.nombre,
    correo: raw.correo ?? raw.email ?? null,
    rol: (raw.rol ?? raw.rol_global ?? 'miembro') as RolGlobal,
    org_id: raw.org_id ?? null,
    org_nombre: raw.org_nombre ?? null,
  };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  token: null,
  user: null,
  status: 'idle',
  error: null,

  hydrate: async () => {
    if (get().status === 'hydrating') return;
    set({ status: 'hydrating', error: null });
    try {
      // 1) Cambiá la cookie de refresh (httpOnly) por un access token nuevo.
      //    Esto cubre la recarga de página y el retorno del OAuth de Google.
      const tk = await apiFetch<{ access_token: string }>('/auth/refresh', {
        method: 'POST',
        skipRefresh: true,
      });
      set({ token: tk.access_token }); // el Bearer ya vive en memoria (C5)
      // 2) Con el Bearer, traé el usuario actual.
      const raw = await apiFetch<RawUser>('/auth/me', { skipRefresh: true });
      set({ user: toAuthUser(raw), status: 'authenticated' });
    } catch {
      set({ token: null, user: null, status: 'anonymous' });
    }
  },

  loginLocal: async ({ correo, password }) => {
    set({ error: null });
    try {
      const data = await apiFetch<RawSession>('/auth/login', {
        method: 'POST',
        // El backend espera `email` (no `correo`).
        json: { email: correo, password },
        skipRefresh: true,
      });
      // Entrar con cuenta propia no hereda la sala del participante anterior
      // que usó este teléfono: se limpia el puntero.
      guardarSala(null);
      set({ token: data.access_token, user: toAuthUser(data.user), status: 'authenticated', error: null });
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : 'No pudimos iniciar tu sesión.';
      set({ error: msg });
      throw err;
    }
  },

  loginSala: async ({ codigo, pin, nombre }) => {
    set({ error: null });
    try {
      const limpio = nombre?.trim();
      const data = await apiFetch<RawSession>('/auth/sala', {
        method: 'POST',
        // El backend espera `codigo_sesion` (no `codigo`). `nombre` es
        // opcional: solo viaja si el participante lo escribió.
        json: {
          codigo_sesion: codigo,
          pin,
          ...(limpio ? { nombre: limpio } : {}),
        },
        skipRefresh: true,
      });
      // EL CÓDIGO MANDA: la sala que abrió este código queda fijada, así el
      // Bolsillo abre ESTA sala y no la que adivinaría por membresía.
      if (data.sala) guardarSala(data.sala);
      set({ token: data.access_token, user: toAuthUser(data.user), status: 'authenticated', error: null });
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : 'No pudimos validar el código de sala.';
      set({ error: msg });
      throw err;
    }
  },

  loginGoogle: (sala?: string) => {
    // Redirección completa: el backend resuelve/crea la identity provider='google'.
    // Con `sala`, el backend la lleva en el state firmado y al volver crea la
    // membresía en el proceso de esa sesión (QR de sala → Google → adentro).
    const qs = sala ? `?sala=${encodeURIComponent(sala)}` : '';
    window.location.href = `${API_BASE}/auth/google/login${qs}`;
  },

  logout: async () => {
    try {
      await apiFetch('/auth/logout', { method: 'POST', skipRefresh: true });
    } catch {
      // Aun si el backend falla, limpiamos el estado local.
    }
    guardarSala(null); // el próximo que use este teléfono no hereda la sala
    set({ token: null, user: null, status: 'anonymous', error: null });
  },

  clearError: () => set({ error: null }),
}));

// Puente con el cliente HTTP: el token en memoria y el manejo de 401.
configureApi({
  getToken: () => useAuthStore.getState().token,
  onTokenRefreshed: (token) => useAuthStore.setState({ token }),
  onAuthLost: () =>
    useAuthStore.setState({ token: null, user: null, status: 'anonymous' }),
});
